import { attackCooldownTicks, attackWindUpTicks, bodyGap, canReach } from './attack.js';
import { assertMover, isPassable } from './clearance.js';
import { resolveAttack } from './combat.js';
import { DEFAULT_POISON_DURATION_TICKS, applyAttack } from './effects.js';
import { add } from './geometry.js';
import { cellIndex } from './grid.js';
import { cellCentre, moveSpeed, stepDistance } from './movement.js';
import { createFlowField, flowAt, hasLineOfSight } from './pathfinding.js';
import { createAttackProfile } from './stats.js';
import { entityById } from './world.js';
/**
 * Monster AI: what one monster wants to do this tick, and nothing else.
 *
 * This module **decides**. `world.ts` moves, `combat.ts` rolls, `effects.ts`
 * settles — every one of them already owns its rule, and a monster that
 * restated any of them would be a second definition of a rule the shared
 * package exists to have exactly one of. So a decision comes back as a
 * `MonsterIntent`: a value the caller hands to `orderMove` or to
 * `resolveMonsterAttack`, never a mutation and never a position.
 *
 * The loop a room runs is therefore three lines, and it is deliberately *there*
 * rather than in here:
 *
 * ```ts
 * const { brain, intent } = decideMonster(world, monster, brains[monster.id], field);
 * if (intent.kind === 'chase') world = orderMove(world, monster.id, intent.destination).world;
 * if (intent.kind === 'attack') queue.schedule(monster.id, intent.targetId, hitTick(brain.windUpTicks, tick));
 * ```
 *
 * The third line is a *schedule* and not a resolution, and that is the whole of
 * `TODO.md` #483: an `attack` intent starts an animation, and the blow lands
 * `windUpTicks` into it at D2's ActionFlag frame. Everything about the swing —
 * both lives, the reach, the target still being on the floor — is judged again
 * on the tick it lands on, and **nothing is drawn from the `Rng` before then**.
 *
 * ## What the design decides, and what this module had to
 *
 * `docs/GAME-DESIGN.md` settles two things outright:
 *
 *   - **Aggro is a radius with no leash.** A monster notices a player inside
 *     `aggroRadius` and chases from that moment — it never gives up and never
 *     returns home, so dragging a train is a tactic rather than a bug. The
 *     radius is a rule about *waking* and nothing else: once a target is held,
 *     distance stops being consulted and no distance ever drops it again.
 *   - **Three archetypes**: melee closes to contact, ranged keeps distance and
 *     shoots, caster uses elemental damage. Here they are a *range table* and
 *     nothing more (`ARCHETYPE_REACH`) — what a caster throws is an
 *     `AttackProfile`, which is data the monster tables own (`TODO.md` #30),
 *     and all five damage types already flow through `resolveAttack`.
 *
 * How far a body reaches is measured, and whether it may swing at all decided,
 * by `attack.ts` — one rule for both sides of a fight, because a player asks the
 * identical question of the identical geometry (`TODO.md` #277). This module
 * only says *whether a monster wants to*.
 *
 * Six it left open, decided here. The first three came out of the first real
 * playtest — *"monsters move too fast, aggro too quickly, keep aggro after
 * death"* — and each one is a rule the design did not state rather than a rule
 * it stated and this module got wrong:
 *
 *   - **A monster is slower than a character** (`ARCHETYPE_SPEED`). Nothing
 *     said so, so every body walked at `DEFAULT_MOVE_SPEED` — and with an aggro
 *     that never leashes, a chase you cannot outpace is a chase that only ends
 *     when somebody dies. Retreat is the move that puts the floor back in the
 *     player's hands, and it only exists if the numbers allow it.
 *   - **Noticing needs a line of sight, chasing does not.** The radius alone
 *     woke monsters through solid rock, which is most of a cave floor. Sight
 *     gates *waking* only: once a target is held the wall between them is a
 *     reason to close (see `canStrike`), never a reason to stop, so a train
 *     dragged round a corner stays dragged.
 *   - **A monster with nothing to hunt goes back to sleep** (`TODO.md` #303).
 *     No leash means a monster never gives up on a *target*; it says nothing
 *     about a target that has ceased to exist. `death.ts` removes a dead
 *     player's body, so a pack that kept `awake` set was still hunting three
 *     seconds later when the character stood back up at the entrance — the one
 *     cell on the floor a party is guaranteed to be on. Losing a quarry settles
 *     a pack, and the radius re-wakes it exactly as it did the first time.
 *   - **Nothing retreats.** A ranged monster that backed away from a closing
 *     player would be unkillable in a corridor, because the same design says it
 *     never gives up; so there is no retreat intent at all. Reach is a
 *     **ceiling, not a band** — a monster already standing inside its target
 *     swings, which is also the state separation can shove it into.
 *   - **A monster stops at its reach rather than inside the body.** Blocking is
 *     one-way by decision (`world.ts`), so nothing physical stops a monster
 *     walking into a player (`TODO.md` #143). The fix is behavioural: a monster
 *     in reach stops asking to move. That is why a reach shorter than one step
 *     is refused at construction — the decision is taken once a tick, so a
 *     chase could otherwise end the tick past a reach it could not see coming.
 *   - **A held target outlives a nearer one.** Re-targeting on distance every
 *     tick would swap a monster between two players standing either side of it,
 *     and would make a train peel off onto whoever walked past. A target is
 *     dropped only when it leaves the world.
 *
 * ## One field for a pack, one search for a single
 *
 * `ARCHITECTURE.md` and the design both say packs chase with a **flow field**,
 * not with N searches: one Dijkstra sweep serves forty skeletons, and each one
 * reads its own cell out of it. So `chaseField` solves the map once per target
 * per body size, and `decideMonster` takes the field as an argument — the
 * caller owns it, because the caller is the one that knows which monsters share
 * a target and a radius.
 *
 * Without a field the destination is the target itself and `world.ts` does the
 * searching, which is the right cost for one monster and the wrong one for a
 * pack. The same fallback catches the two cases a field cannot answer: one
 * solved on somebody else (a monster that re-targeted mid-tick outlives its
 * pack's field), and the target's own cell, which has no step out of it.
 *
 * ## Determinism
 *
 * Nothing here draws from the `Rng`. A decision is a pure function of the world
 * and the brain, so the client and the server take the same one and — crucially
 * — the random stream is where combat left it. `combat.ts`'s fixed roll budget
 * rests on that: an AI tick that drew a number would reseat every swing after
 * it on one side of the wire and not the other. Ties are broken by **entity
 * id**, never by list order: `World.entities` is an array here and a
 * `MapSchema` on the wire (`TODO.md` #106), so order is exactly what the two
 * sides may disagree about.
 */
/** The three the design names. Melee closes to contact; the other two shoot from range. */
export const MONSTER_ARCHETYPES = ['melee', 'ranged', 'caster'];
/**
 * How far a monster notices a player, in cells of **clear air between the two
 * bodies** — not between their centres, and only along a line it can see down.
 *
 * Measured from the skin so a fat monster notices further out than a small one
 * standing in the same place, which is the same measurement melee reach uses
 * (`bodyGap`).
 *
 * **Five**, down from eight, because eight was the larger half of *"aggro too
 * quickly"*. What a radius really costs is area: a monster with a half-cell body
 * claimed a disc nine cells across, some 250 cells of a 64×64 floor, and sixty
 * of those overlapping meant walking anywhere woke several packs that then had
 * no reason ever to stop. Five claims 113 — **fifty-five per cent less ground
 * each** — so a floor is crossed a pack at a time.
 *
 * Five is also the floor the rest of this module allows: `ARCHETYPE_REACH`
 * stands a ranged monster off at 4.5, and a monster that could only shoot from
 * further than it can notice would walk at its target forever. Taking the radius
 * lower means taking that reach down with it.
 *
 * A placeholder in exactly the sense `DEFAULT_MOVE_SPEED` is one: monster tables
 * will carry a per-monster radius when they exist (`TODO.md` #30).
 */
export const DEFAULT_AGGRO_RADIUS = 5;
/**
 * How close each archetype wants to be, in cells of clear air.
 *
 * A range table and nothing else. Melee stands at arm's length — half a cell of
 * air, so two ordinary bodies sit a cell and a half apart, centre to centre.
 * The two shooters stand off, and both stand well inside the aggro radius:
 * a monster that could only shoot from further than it can notice would walk
 * towards its target forever.
 *
 * Placeholders, like every other number here. What separates a caster from an
 * archer for real is the damage it throws, which is an `AttackProfile` and
 * therefore monster-table data rather than a branch in this module.
 */
export const ARCHETYPE_REACH = {
    melee: 0.5,
    caster: 3.5,
    ranged: 4.5,
};
/**
 * How fast each archetype walks before anything modifies it, in cells per
 * second — the **base** half of the speed stat (`movement.ts`'s `moveSpeed`).
 *
 * It sits beside `ARCHETYPE_REACH` because it is the same kind of decision and
 * the mirror of it: **the further a monster strikes from, the less ground it
 * ever has to make up.** Melee has to arrive at contact and is the quickest of
 * the three; a shooter's job is to reach its range and stand in it, and one that
 * could also run a player down would make standing off pointless.
 *
 * Every one of them is under `DEFAULT_MOVE_SPEED`, and that is the whole point.
 * A monster spawned at a character's own speed is a monster nothing can be done
 * about: aggro has no leash by design, so if it also cannot be outpaced, the
 * only end to a chase is a corpse. At melee's three cells a second a running
 * player gains one cell each second — enough to reach a door, drink, or choose
 * the ground, and slow enough that fleeing costs real time and is a decision
 * rather than a free escape.
 *
 * Placeholders, like every other number here, and the ones most likely to move:
 * they are what a player *feels* every second of a floor. An elite's Extra Fast
 * doubles whichever of them it lands on, which is what makes that affix mean
 * "this one you do not walk away from".
 */
export const ARCHETYPE_SPEED = {
    melee: 3,
    caster: 2.5,
    ranged: 2,
};
/**
 * The cadence of a monster nothing has said anything about: a neutral profile,
 * converted.
 *
 * Twelve ticks — 0.6 s — and a placeholder for the same reason everything else
 * here is one. Monster tables will carry real weapon speeds and animation
 * lengths (`TODO.md` #22, #30), at which point a brain takes
 * `attackCooldownTicks(profile)` and this default stops being reached for.
 */
export const DEFAULT_ATTACK_TICKS = attackCooldownTicks(createAttackProfile());
/**
 * How long that same neutral swing spends winding up before the blow lands.
 *
 * Seven of the twelve, and taken from **the same profile** the twelve came from
 * rather than stated beside it: a monster table that gives a skeleton a faster
 * weapon shortens both together, because both are readings of one animation
 * through one accumulator (`combat.ts`'s `framesToHit`). A number written here
 * would be a telegraph that stopped matching its own swing the moment anything
 * was tuned.
 */
export const DEFAULT_WIND_UP_TICKS = attackWindUpTicks(createAttackProfile());
/**
 * The shortest reach a brain may carry: one of **its own** steps' worth of
 * ground.
 *
 * A decision is taken once a tick and a step is spent whole, so a monster whose
 * reach is shorter than a step can close from *outside* it to *past* it without
 * ever seeing the tick where it should have stopped — and would then stand
 * inside the body it meant to stop at. Refused at construction rather than
 * discovered on screen.
 *
 * It used to be measured against `DEFAULT_MOVE_SPEED`, on the grounds that a
 * brain is built before it is attached to anything and every entity walked at
 * that one number. Neither half is true any more: a brain resolves its own
 * speed (below), and a modifier can raise it above a character's. So the floor
 * is measured against the speed this monster will actually walk at, which is
 * what `TODO.md` #241 asked for.
 */
const minReach = (speed) => stepDistance(speed);
const assertRange = (what, value, low) => {
    // Phrased as a negated comparison so NaN is rejected along with the
    // out-of-range values: a NaN reach would compare false against every gap and
    // leave a monster walking into its target forever.
    if (!(Number.isFinite(value) && value >= low)) {
        throw new RangeError(`${what} must be a finite number of at least ${low}, received ${value}`);
    }
};
const assertTicks = (value) => {
    if (!(Number.isInteger(value) && value >= 1)) {
        throw new RangeError(`A swing must cost a whole number of at least 1 tick, received ${value}`);
    }
};
/**
 * The wind-up has to land the blow inside the swing that threw it.
 *
 * Zero is allowed and means an animation with no anticipation at all — the
 * state this game was in before a hit frame existed, kept expressible so that
 * having one is a decision rather than a constant (`attack.ts`).
 */
const assertWindUp = (value, attackTicks) => {
    if (!(Number.isInteger(value) && value >= 0 && value <= attackTicks)) {
        throw new RangeError(`A wind-up must be a whole number of ticks between 0 and the ${attackTicks} its swing lasts, received ${value}`);
    }
};
/**
 * A brain, asleep, with nothing noticed and ready to swing.
 *
 * The archetype picks the reach; every number is overridable, because monster
 * tables will want to (`TODO.md` #30) and because a test that has to walk a
 * monster across a hall to see one swing is a slow test. Nonsense is refused
 * here rather than carried into a walk: this is the constructor, and a brain
 * that exists is a brain whose numbers a tick can be reasoned about.
 */
export const createMonsterBrain = (input) => {
    const aggroRadius = input.aggroRadius ?? DEFAULT_AGGRO_RADIUS;
    const reach = input.reach ?? ARCHETYPE_REACH[input.archetype];
    const attackTicks = input.attackTicks ?? DEFAULT_ATTACK_TICKS;
    // An unstated wind-up is fitted into the cadence the caller did state; one
    // stated outright is judged against it below. The difference is the same one
    // every default here draws: filling a blank in is this constructor's job,
    // and silently correcting a number somebody wrote down is not.
    const windUpTicks = input.windUpTicks ?? Math.min(DEFAULT_WIND_UP_TICKS, attackTicks);
    // Resolved before the reach is judged, because how fast this monster walks is
    // what decides whether that reach is long enough to stop it in time.
    const speed = moveSpeed(input.speed ?? ARCHETYPE_SPEED[input.archetype], input.modifiers ?? []);
    assertRange('A reach', reach, minReach(speed));
    // A body must notice at least as far as it can strike from. This is the rule
    // `DEFAULT_AGGRO_RADIUS`'s floor was only ever a consequence of — a ranged
    // monster stands off at 4.5, so a radius under that leaves it walking at
    // something it can never engage: no error, no crash, just a monster that
    // never fights. It was written in a comment and enforced nowhere until a
    // review said so, which left tuning the radius one edit from a silent
    // gameplay bug. Judged per brain rather than against a constant, so it holds
    // for any archetype and survives both tables being retuned.
    assertRange('An aggro radius', aggroRadius, reach);
    assertTicks(attackTicks);
    assertWindUp(windUpTicks, attackTicks);
    return {
        archetype: input.archetype,
        awake: false,
        targetId: null,
        recovery: 0,
        aggroRadius,
        reach,
        speed,
        attackTicks,
        windUpTicks,
    };
};
/** Whether this entity is something a monster hunts. Players only, and never itself. */
const isPrey = (self, candidate) => candidate.kind === 'player' && candidate.id !== self.id;
/**
 * The better of two candidates: the nearer one, and on an exact tie the one
 * with the smaller id.
 *
 * Two players equally close is not a rare case — it is a doorway with one
 * either side of it — and the tie has to be broken by something the server and
 * every client hold identically. The id is the only such thing: list order is
 * exactly what a `MapSchema` does not promise (`TODO.md` #106).
 */
const nearer = (self, candidate, best) => {
    if (best === null)
        return candidate;
    const difference = bodyGap(self, candidate) - bodyGap(self, best);
    const wins = difference === 0 ? candidate.id < best.id : difference < 0;
    return wins ? candidate : best;
};
/**
 * Whether this monster would notice that player from where it stands: near
 * enough, and down a line it can actually see.
 *
 * Two conditions, in that order, because the cheap one throws away nearly every
 * candidate: a floor of sixty monsters asks this every tick, and the sight trace
 * is only ever paid for somebody already inside the radius.
 *
 * Sight is traced as a **point**, without either body — the same rule
 * `attack.ts` states about a blow, for the same reason. A monster is not blinded
 * by a corridor it merely cannot walk down, and a glance round a corner is not a
 * body squeezing round it.
 *
 * The trace is what makes the radius mean anything on a cave floor. Without it,
 * a radius is a disc through solid rock: a monster two rooms away, with no route
 * to a player and no sight of one, woke and started walking — which is a large
 * part of what *"aggro too quickly"* actually was.
 */
const notices = (world, self, candidate, radius) => bodyGap(self, candidate) <= radius && hasLineOfSight(world.grid, self.position, candidate.position);
/**
 * Who this monster is after: the target it already holds, or the nearest player
 * it notices.
 *
 * The held target wins outright, and is dropped only when it leaves the world —
 * see the header for why re-targeting on distance is the wrong rule. **Nothing
 * about a held target is ever re-examined**: not the distance to it, not the
 * wall in front of it. That is the whole of "no leash", and it is why a train
 * dragged across a floor and round a corner stays dragged.
 *
 * When there is nothing held, the radius and the sight line apply — to a woken
 * monster exactly as to a sleeping one. They used to apply only while asleep, so
 * a monster that had just lost its quarry took the nearest player anywhere on
 * the floor; that is the rule that made killing a player *re-point* a pack
 * rather than settle it (`TODO.md` #303), and it is gone.
 *
 * `null` — nobody to chase — is an ordinary answer, not a failure: an empty
 * floor, a player who portalled out, a monster that has not noticed anyone yet.
 */
export const chooseTarget = (world, self, brain) => {
    const held = brain.targetId === null ? null : entityById(world, brain.targetId);
    if (held !== null)
        return held;
    return world.entities.reduce((best, candidate) => isPrey(self, candidate) && notices(world, self, candidate, brain.aggroRadius)
        ? nearer(self, candidate, best)
        : best, null);
};
/**
 * The map solved towards one target, for one body size — what a pack chases.
 *
 * One sweep serves every monster of that radius chasing that player, which is
 * the whole reason it exists: N monsters would otherwise cost N searches, every
 * tick, for the same answer. The caller builds it once per tick per (target,
 * radius) group and hands it to `decideMonster`.
 *
 * `null` means **this body cannot be led to that target at all** — a player
 * down a crack an ogre does not fit in. That is an ordinary outcome rather than
 * an error, and the pack falls back to walking at the target itself, which
 * `orderMove` turns into "walk as close as you can". A mover measured against a
 * different map is *not* ordinary and throws, exactly as `findPath` does.
 */
export const chaseField = (world, target, mover) => {
    assertMover(world.grid, mover);
    // `createFlowField` throws on a target this body could not stand on, and a
    // target standing somewhere its chasers cannot follow is an ordinary state.
    if (!isPassable(world.grid, target.position, mover))
        return null;
    return createFlowField(world.grid, target.position, mover);
};
/** Whether a field was solved on the cell this target is standing in right now. */
const solvedFor = (world, field, target) => cellIndex(world.grid, field.target) === cellIndex(world.grid, target.position);
/**
 * Where to walk to close on a target: one step down the field, or the target
 * itself.
 *
 * The field step is read as the **neighbouring cell's centre**, which is what a
 * waypoint means everywhere in core (`cellCentre`) and what the sweep proved
 * this body can stand on. Three things send it to the target instead — no field
 * at all, a field solved on somebody else, and a field with no step out of the
 * cell this monster is in (the target's own cell, or ground the sweep never
 * reached). All three are the same answer for the same reason: `orderMove` can
 * search for one monster, and a monster whose field cannot answer is one
 * monster.
 */
const chaseDestination = (world, self, target, field) => {
    if (field === null || !solvedFor(world, field, target))
        return target.position;
    const step = flowAt(field, self.position);
    if (step.x === 0 && step.y === 0)
        return target.position;
    return cellCentre(add(cellCentre(self.position), step));
};
/**
 * Whether this monster could land a blow on that target from where it stands.
 *
 * The reach rule itself is `attack.ts`'s and is asked here rather than restated:
 * a monster's reach and a character's are the same question about the same two
 * discs, and a second copy of it would be a rule the client and the server could
 * one day disagree about (`TODO.md` #277).
 *
 * What is monster-specific is what a *failure* means. A monster that stood
 * waiting for a target it cannot see would never fight at all — so a blocked
 * line is a reason to **close**, and the chase is what eventually clears it.
 */
const canStrike = (world, self, target, brain) => canReach(world, self, target, brain.reach);
/**
 * Recovery, one tick older.
 *
 * It runs down whatever the monster is doing — walking, standing, or looking
 * for somebody new. Recovery is an animation, not a resource, which is why a
 * monster that spends the last of its cooldown closing the final cell swings on
 * arrival rather than paying for the cooldown twice.
 */
const cooled = (brain) => Math.max(0, brain.recovery - 1);
/**
 * One monster, one tick of thought.
 *
 * Pure, and it draws nothing from the random stream. The brain comes back
 * beside the intent rather than being mutated, like every other state in core:
 * the caller keeps it between ticks, which is what makes `awake`, the held
 * target and the swing cadence survive without this module owning a collection.
 *
 * The order is the whole of the behaviour:
 *
 *   1. **Who** — the held target, or the nearest player noticed inside the
 *      radius. Nobody means idle, **and idle settles it**: a monster with
 *      nothing to hunt drops back to where it spawned, mentally, and the radius
 *      is what wakes it again (`TODO.md` #303).
 *   2. **Can it strike from here** — reach *and* line of sight. Reach is a
 *      ceiling and not a band, so a monster standing inside its target swings.
 *   3. **Is it recovered** — start a swing, or hold the ground it has. A monster
 *      in reach never asks to move again, which is what stops it walking into
 *      the body it came to hit (`TODO.md` #143). The blow itself lands
 *      `windUpTicks` after the `attack` intent, not on it.
 *   4. **Otherwise close** — one step down the pack's field, or straight at the
 *      target when there is no field to read.
 *
 * A field built for another floor is programmer error and throws: it would
 * otherwise be silently unusable — every cell off the map, every step zero —
 * and the monster would walk at its target as if no field had been passed at
 * all, which is the bug wearing the fallback's clothes.
 */
export const decideMonster = (world, self, brain, field = null) => {
    if (field !== null && field.grid !== world.grid) {
        throw new RangeError('Chase field was solved on a different map than the one the monster is standing on');
    }
    const target = chooseTarget(world, self, brain);
    if (target === null) {
        // Settled, not merely idle. The recovery clock still runs down, because it
        // is an animation finishing rather than a thing being hunted.
        return {
            brain: { ...brain, awake: false, targetId: null, recovery: cooled(brain) },
            intent: { kind: 'idle' },
        };
    }
    const engaged = { ...brain, awake: true, targetId: target.id };
    if (!canStrike(world, self, target, brain)) {
        return {
            brain: { ...engaged, recovery: cooled(brain) },
            intent: {
                kind: 'chase',
                targetId: target.id,
                destination: chaseDestination(world, self, target, field),
            },
        };
    }
    return brain.recovery === 0
        ? {
            // The swing costs the whole cadence, of which this tick is the first —
            // the tick the *animation* starts, with the blow `windUpTicks` further
            // on: `attackTicks` of 1 is a swing every tick, and never a tick of
            // nothing.
            brain: { ...engaged, recovery: brain.attackTicks - 1 },
            intent: { kind: 'attack', targetId: target.id },
        }
        : { brain: { ...engaged, recovery: cooled(brain) }, intent: { kind: 'hold', targetId: target.id } };
};
/**
 * A monster's swing, rolled and landed on the body in one call.
 *
 * Nothing but composition — `resolveAttack` rolls it and `applyAttack` settles
 * it — with one rule carried across the seam: the source is `'monster'`, so a
 * second poison **adds** to the one already running instead of replacing it
 * (`effects.ts`, `TODO.md` #230). `applyAttack` defaults to the character rule,
 * which means a caller that wired the two together by hand and forgot the
 * source would silently give every monster in the game a player's poison. That
 * is the one thing this wrapper exists to make impossible.
 *
 * The roll budget is `resolveAttack`'s, untouched: this draws exactly what a
 * swing draws, in the same order, so a replay reproduces it.
 */
export const resolveMonsterAttack = (attacker, defender, vitals, rng, poisonDurationTicks = DEFAULT_POISON_DURATION_TICKS) => {
    const result = resolveAttack(attacker, defender, rng);
    return { result, change: applyAttack(vitals, result, poisonDurationTicks, 'monster') };
};
//# sourceMappingURL=monster.js.map